import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import cookie from 'react-cookies';
import Container from '../../components/container';
import Textfield from '../../components/textfield';
import Button from '../../components/button';
import CodeInput from '../../components/code.input';

import './sign.scss';
import useGlobal from '../../../store';

function acceptableEmail(value) {

  if (typeof value !== 'string') return false;
  const email = value.trim().toLowerCase();
  if (email.length < 3 || email.length > 255) return false;
  if (email.indexOf(' ') !== -1) return false;
  const at = email.indexOf('@');
  if (at < 1 || at !== email.lastIndexOf('@')) return false;
  const domain = email.slice(at + 1);
  if (!domain) return false;
  const dot = domain.lastIndexOf('.');
  if (dot < 1 || domain.length - dot - 1 < 2) return false;
  return true;

}

function formatWait(seconds) {

  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${ minutes }:${ rest < 10 ? '0' : '' }${ rest }`;

}

const Sign = () => {

  const [ globalState, globalActions ] = useGlobal();
  const { sign } = globalActions;

  const [ email, setEmail ] = useState('');
  const [ password, setPassword ] = useState('');
  const [ error, setError ] = useState('');
  const [ step, setStep ] = useState('email');
  const [ sentEmail, setSentEmail ] = useState('');
  const [ passwordAllowed, setPasswordAllowed ] = useState(false);
  const [ pending, setPending ] = useState(false);
  const [ seconds, setSeconds ] = useState(0);
  const [ codeInvalid, setCodeInvalid ] = useState(false);
  const [ codeNonce, setCodeNonce ] = useState(0);
  const pendingRef = useRef(false);

  const navigate = useNavigate();
  const emailOk = acceptableEmail(email);

  useEffect( () => { 
    
    const token = cookie.load('token');
    token && sign.Check( token, () => { navigate('/'); });
  
  }, []);

  useEffect( () => { error !== '' && setError(''); }, [ email, password ]);

  useEffect(() => {

    setPasswordAllowed(false);
    if (!emailOk) return undefined;
    let cancelled = false;
    sign.Options(email.trim().toLowerCase()).then((allowed) => {
      if (!cancelled) setPasswordAllowed(allowed === true);
    });
    return () => { cancelled = true; };

  }, [ email ]);

  useEffect(() => {

    if (seconds <= 0) return undefined;
    const timer = setTimeout(() => setSeconds((value) => value - 1), 1000);
    return () => clearTimeout(timer);

  }, [ seconds ]);

  function begin() {

    if (pendingRef.current) return false;
    pendingRef.current = true;
    setPending(true);
    return true;

  }

  function finish() {

    pendingRef.current = false;
    setPending(false);

  }

  function showRequestError(text) {

    setError(typeof text === 'string' && text ? text : 'Нет соединения с сервером');
    finish();

  }

  function sendCode() {

    if (!emailOk || pendingRef.current) return;
    begin();
    const normalized = email.trim().toLowerCase();
    sign.SendCode(normalized, () => {
      setSentEmail(normalized);
      setStep('code');
      setSeconds(60);
      setCodeInvalid(false);
      setError('');
      setCodeNonce((value) => value + 1);
      finish();
    }, showRequestError);

  }

  function confirmCode(code) {

    if (!begin()) return false;
    setCodeInvalid(false);
    setError('');
    sign.ConfirmCode(sentEmail, code, () => {
      finish();
      navigate('/');
    }, (text) => {
      setCodeInvalid(true);
      showRequestError(text);
    });
    return true;

  }

  function signIn() {

    if (!passwordAllowed || password.length < 3 || !begin()) return;
    sign.In(email.trim().toLowerCase(), password, () => {
      finish();
      navigate('/');
    }, showRequestError);

  }

  return (

    <Container className = "sign">

      <div className="sign__big_logo">

        3D Балкон
        <span>{ step === 'code' ? 'Код отправлен' : 'Авторизация' }</span>  

      </div>

      { step === 'email' && (

        <>

          <Textfield

            title = "E-mail:"
            value = { email }
            set = { setEmail }
            onEnter = { emailOk ? sendCode : () => {} }

          />

          { passwordAllowed && (

            <Textfield

              title = "Пароль:"
              type = "password"
              value = { password }
              set = { setPassword }

            />

          ) }

          { error.length > 0 && <div className = "error text-center">{ error }</div> }

          <Button

            text = "Получить код"
            action = { sendCode }
            disabled = { !emailOk }

          />

          { passwordAllowed && (

            <Button

              text = "Войти"
              action = { signIn }
              disabled = { password.length < 3 }

            />

          ) }

        </>

      ) }

      { step === 'code' && (

        <>

          <div className="sign__sent">Мы отправили код <br/>на адрес { sentEmail }</div>

          <br/>
          <div className="sign__sent">Введите его, чтобы войти:</div>
          <br/>

          <CodeInput

            key = { codeNonce }
            onComplete = { confirmCode }
            onEdit = { () => { setCodeInvalid(false); setError(''); } }
            invalid = { codeInvalid }
            busy = { pending }

          />

          <div className="sign__actions">

            <div className="sign__actions__spam">Если письма нет, проверьте папку "Спам"</div>

            <button
              type="button"
              className={ seconds > 0 ? 'is-wait' : '' }
              onClick={ () => { if (seconds <= 0) sendCode(); } }
            >
              { seconds > 0 ? `Отправить письмо с кодом ещё раз через ${ formatWait(seconds) }` : 'Отправить письмо с кодом ещё раз' }
            </button>

            <hr/>

            <button
              type="button"
              onClick={ () => {
                if (pendingRef.current) return;
                setStep('email');
                setCodeInvalid(false);
                setError('');
              } }
            >
              Вернуться и изменить E-mail
            </button>

          </div>

          { error.length > 0 && <div className = "error text-center">{ error }</div> }

        </>

      ) }

    </Container>

  )

}

export default Sign;
